/// <amd-module name="Feed/Legacy/Protocols/ProtocolProvider" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { MavenPackageProtocol } from "Feed/Legacy/Protocols/Maven/MavenPackageProtocol";
import { NpmPackageProtocol } from "Feed/Legacy/Protocols/Npm/NpmPackageProtocol";
import { NuGetPackageProtocol } from "Feed/Legacy/Protocols/NuGet/NuGetPackageProtocol";
import { UPackPackageProtocol } from "Protocols/UPack/UPackPackageProtocol";
import { IvyPackageProtocol } from "./Ivy/IvyPackageProtocol";
import { PyPiPackageProtocol } from "./PyPi/PyPiPackageProtocol";

export interface IFeedProtocolProvider extends IVssService {
    get(protocolType: string, throwIfMissing?: boolean): IPackageProtocol;

    getEnabledProtocolTypes(): { [key: string]: IPackageProtocol };

    initializeProtocols(): void;
}

export class ProtocolProvider extends VssService implements IFeedProtocolProvider {
    private static _packageProtocols: { [key: string]: IPackageProtocol };
    private static nameMap: { [key: string]: string };

    public initializeProtocols(): void {
        const packageProtocols: { [key: string]: IPackageProtocol } = {};
        ProtocolProvider.nameMap = {};

        const addProtocol = (protocol: IPackageProtocol) => {
            ProtocolProvider.nameMap[protocol.key.toLowerCase()] = protocol.key;
            packageProtocols[protocol.key] = protocol;
        };

        addProtocol(new NuGetPackageProtocol(this.pageContext));
        addProtocol(new NpmPackageProtocol(this.pageContext));
        addProtocol(new MavenPackageProtocol(this.pageContext));
        addProtocol(new PyPiPackageProtocol(this.pageContext));
        addProtocol(new UPackPackageProtocol(this.pageContext));

        if (this.HubWebPageDataService.isIvyUIEnabled()) {
            addProtocol(new IvyPackageProtocol(this.pageContext));
        }

        ProtocolProvider._packageProtocols = packageProtocols;
    }

    public get(protocolType: string, throwIfMissing = false): IPackageProtocol {
        const protocols = ProtocolProvider._packageProtocols;

        const key = ProtocolProvider.nameMap[protocolType.toLowerCase()];
        const protocol = (key && protocols[key]) || null;
        if (!protocol && throwIfMissing) {
            throw Error(`Couldn't find protocol provider for ${protocolType}`);
        }

        return protocol;
    }

    public getProtocols(): IPackageProtocol[] {
        // Ideally this would be "Object.values(ProtocolProvider._packageProtocols)", but .values is not supported yet
        const protocols = ProtocolProvider._packageProtocols;
        return Object.keys(protocols).map(key => protocols[key]);
    }

    public getEnabledProtocolTypes(): { [key: string]: IPackageProtocol } {
        return { ...ProtocolProvider._packageProtocols };
    }

    private get HubWebPageDataService(): HubWebPageDataService {
        return this.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }
}

Services.add("IFeedProtocolProvider", { serviceFactory: ProtocolProvider });
